/**
 * Simple API Client with Bearer token authentication
 *
 * Usage:
 *   import { apiClient } from '@/services/apiClient'
 *   const response = await apiClient.get('/api/workspaces')
 *   const data = await apiClient.post('/api/workspaces', { name: 'My Workspace' })
 */

import { resolveApiBaseUrl } from '../config/apiBaseUrl'
import { getAuthApiService } from './authApi'
import { emitSessionExpired } from './sessionEvents'

// Prefer same-origin by default (works with Vite proxy and avoids mixed-content under HTTPS).
const BASE_URL = resolveApiBaseUrl()

// Request cache to prevent duplicate API calls
const requestCache = new Map<string, { data: any; timestamp: number; inFlight?: Promise<any> }>()
const CACHE_TTL_MS = 10000 // Cache for 10 seconds

const CACHEABLE_PATTERNS = [
  /\/storybooks\/[^/]+\/chapters$/,
  /\/workspaces\/[^/]+\/white-label$/,
  /\/workspaces\/[^/]+$/,
]

export interface ApiErrorEnvelope {
  code?: string
  category?: string
  kind?: string
  title?: string
  message?: string
  retryable?: boolean
  actions?: string[]
  userActions?: string[]
  fieldHints?: Record<string, string>
  correlationId?: string
  requestId?: string
  technicalDetails?: string
  technicalMessage?: string
}

export class ApiClientError extends Error {
  status: number
  code?: string
  category?: string
  kind?: string
  title?: string
  retryable?: boolean
  actions: string[]
  fieldHints: Record<string, string>
  correlationId?: string
  technicalDetails?: string
  rawBody?: unknown

  constructor(params: {
    message: string
    status: number
    code?: string
    category?: string
    kind?: string
    title?: string
    retryable?: boolean
    actions?: string[]
    fieldHints?: Record<string, string>
    correlationId?: string
    technicalDetails?: string
    rawBody?: unknown
  }) {
    super(params.message)
    this.name = 'ApiClientError'
    this.status = params.status
    this.code = params.code
    this.category = params.category
    this.kind = params.kind
    this.title = params.title
    this.retryable = params.retryable
    this.actions = params.actions ?? []
    this.fieldHints = params.fieldHints ?? {}
    this.correlationId = params.correlationId
    this.technicalDetails = params.technicalDetails
    this.rawBody = params.rawBody
  }
}

function shouldCache(url: string): boolean {
  return CACHEABLE_PATTERNS.some((pattern) => pattern.test(url))
}

function extractApiError(body: any, fallback: string): string {
  if (body?.error?.message && typeof body.error.message === 'string') return body.error.message
  if (typeof body?.error === 'string') return body.error
  if (body?.message && typeof body.message === 'string') return body.message
  return fallback
}

function buildApiClientError(status: number, body: any, fallback: string): ApiClientError {
  const envelope: ApiErrorEnvelope = body?.error && typeof body.error === 'object' ? body.error : {}

  return new ApiClientError({
    status,
    message: extractApiError(body, fallback),
    code: envelope.code,
    category: envelope.category,
    kind: envelope.kind,
    title: envelope.title,
    retryable: envelope.retryable,
    actions: envelope.actions ?? envelope.userActions ?? [],
    fieldHints: envelope.fieldHints ?? {},
    correlationId: envelope.correlationId ?? envelope.requestId,
    technicalDetails: envelope.technicalDetails ?? envelope.technicalMessage,
    rawBody: body,
  })
}

function getAuthToken(): string | null {
  return localStorage.getItem('ignis_auth_token')
}

function getHeaders(): HeadersInit {
  const authToken = getAuthToken()

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  return headers
}

async function parseErrorBody(response: Response): Promise<any> {
  return response.json().catch(() => ({ message: response.statusText }))
}

/**
 * Thin fetch wrapper that handles a single 401 refresh-and-retry.
 * Hard loop guard: at most ONE retry per call, never recursive.
 * On definitive auth failure (null token or second 401) emits the
 * ignis:session-expired event and returns the failed Response so the
 * existing per-method error handling can proceed unchanged.
 */
async function fetchWithAuthRetry(fullUrl: string, init: RequestInit): Promise<Response> {
  const response = await fetch(fullUrl, init)
  if (response.status !== 401) return response

  // First 401 — attempt a silent token refresh
  const newToken = await getAuthApiService().refreshSessionSilently()
  if (!newToken) {
    emitSessionExpired()
    return response
  }

  // Rebuild the Authorization header with the new token; preserve everything else exactly
  const existingHeaders = new Headers(init.headers)
  existingHeaders.set('Authorization', `Bearer ${newToken}`)
  const retried = await fetch(fullUrl, { ...init, headers: existingHeaders })

  if (retried.status === 401) {
    emitSessionExpired()
  }
  return retried
}

async function get<T = any>(url: string): Promise<T> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  if (shouldCache(url)) {
    const cached = requestCache.get(url)
    const now = Date.now()

    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      return cached.data as T
    }

    if (cached?.inFlight) {
      return cached.inFlight as Promise<T>
    }

    const fetchPromise = (async () => {
      const response = await fetchWithAuthRetry(fullUrl, {
        method: 'GET',
        headers: getHeaders(),
      })

      if (!response.ok) {
        const error = await parseErrorBody(response)
        throw buildApiClientError(error?.status ?? response.status, error, `HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      requestCache.set(url, { data, timestamp: Date.now() })
      return data
    })()

    requestCache.set(url, {
      ...cached,
      inFlight: fetchPromise,
      timestamp: cached?.timestamp || 0,
      data: cached?.data,
    })

    try {
      return (await fetchPromise) as T
    } catch (error) {
      requestCache.delete(url)
      throw error
    }
  }

  const response = await fetchWithAuthRetry(fullUrl, {
    method: 'GET',
    headers: getHeaders(),
  })

  if (!response.ok) {
    const error = await parseErrorBody(response)
    throw buildApiClientError(response.status, error, `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

async function post<T = any>(url: string, data?: any, options?: Pick<RequestInit, 'signal'>): Promise<T> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  const response = await fetchWithAuthRetry(fullUrl, {
    method: 'POST',
    headers: getHeaders(),
    body: data ? JSON.stringify(data) : undefined,
    ...(options?.signal ? { signal: options.signal } : {}),
  })

  if (!response.ok) {
    const error = await parseErrorBody(response)
    throw buildApiClientError(response.status, error, `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

async function put<T = any>(url: string, data?: any): Promise<T> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  const response = await fetchWithAuthRetry(fullUrl, {
    method: 'PUT',
    headers: getHeaders(),
    body: data ? JSON.stringify(data) : undefined,
  })

  if (!response.ok) {
    const error = await parseErrorBody(response)
    throw buildApiClientError(response.status, error, `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

async function patch<T = any>(url: string, data?: any): Promise<T> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  const response = await fetchWithAuthRetry(fullUrl, {
    method: 'PATCH',
    headers: getHeaders(),
    body: data ? JSON.stringify(data) : undefined,
  })

  if (!response.ok) {
    const error = await parseErrorBody(response)
    throw buildApiClientError(response.status, error, `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

async function del<T = any>(url: string): Promise<T> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  const response = await fetchWithAuthRetry(fullUrl, {
    method: 'DELETE',
    headers: getHeaders(),
  })

  if (!response.ok) {
    const error = await parseErrorBody(response)
    throw buildApiClientError(response.status, error, `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

async function request<T = any>(url: string, options: RequestInit = {}): Promise<T> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  const headers = {
    ...getHeaders(),
    ...(options.headers || {}),
  }

  const response = await fetchWithAuthRetry(fullUrl, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await parseErrorBody(response)
    throw buildApiClientError(response.status, error, `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

async function postFormData<T = any>(url: string, formData: FormData): Promise<T> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  const authToken = getAuthToken()
  const headers: HeadersInit = {}

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  const response = await fetch(fullUrl, {
    method: 'POST',
    headers,
    body: formData,
  })

  if (!response.ok) {
    const error = await parseErrorBody(response)
    throw buildApiClientError(response.status, error, `HTTP ${response.status}: ${response.statusText}`)
  }

  return response.json()
}

async function downloadFile(url: string, options: RequestInit = {}): Promise<Response> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  const baseHeaders = getHeaders() as Record<string, string>
  delete baseHeaders['Content-Type']

  const headers = {
    ...baseHeaders,
    ...(options.headers || {}),
  }

  const response = await fetch(fullUrl, {
    method: options.method || 'GET',
    ...options,
    headers,
  })

  if (!response.ok) {
    const error = await parseErrorBody(response)
    throw buildApiClientError(response.status, error, `HTTP ${response.status}: ${response.statusText}`)
  }

  return response
}

/** Open an authenticated response body for SSE/NDJSON consumers. */
async function openStream(url: string, options: RequestInit = {}, controls?: { maxErrorBytes: number }): Promise<Response> {
  if (controls && (!Number.isSafeInteger(controls.maxErrorBytes) || controls.maxErrorBytes < 1 || controls.maxErrorBytes > 1024 * 1024)) {
    throw new Error('Invalid stream error response allowance')
  }
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`
  const headers = {
    ...getHeaders(),
    ...(options.headers || {}),
  }
  const response = await fetchWithAuthRetry(fullUrl, {
    ...options,
    headers,
  })
  if (!response.ok) {
    const error = controls ? await parseBoundedStreamError(response, controls.maxErrorBytes, options.signal) : await parseErrorBody(response)
    throw buildApiClientError(response.status, error, `HTTP ${response.status}: ${response.statusText}`)
  }
  return response
}

/** Opt-in for bounded consumers; HTTP status remains authoritative even when a
 * body is absent, malformed or too large. Existing callers keep their contract. */
async function parseBoundedStreamError(response: Response, maximum: number, signal?: AbortSignal | null): Promise<unknown> {
  const reader = response.body?.getReader()
  if (!reader) return {}
  const decoder = new TextDecoder('utf-8', { fatal: true })
  let bytes = 0, text = '', finished = false
  const abort = () => { void reader.cancel().catch(() => undefined) }
  signal?.addEventListener('abort', abort, { once: true })
  try {
    while (true) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
      const chunk = await reader.read()
      if (chunk.done) break
      bytes += chunk.value.byteLength
      if (bytes > maximum) return {}
      text += decoder.decode(chunk.value, { stream: true })
    }
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
    text += decoder.decode()
    const parsed: unknown = JSON.parse(text)
    finished = true
    return parsed
  } catch {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
    return {}
  } finally {
    signal?.removeEventListener('abort', abort)
    if (!finished) void reader.cancel().catch(() => undefined)
    reader.releaseLock()
  }
}

export const apiClient = {
  get,
  post,
  postFormData,
  put,
  patch,
  delete: del,
  request,
  downloadFile,
  openStream,
  getHeaders,
}

export { get, post, postFormData, put, patch, del as delete, request, downloadFile, openStream, getHeaders }
